import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Dashboard } from '../../models/Dashboard'
import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { ItemSubType } from '../../types/models'
import { getIconNameFromType, plural } from '../../utils/item'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { Icon, IconName } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSApplication } from '../app/PSApplication'
import Label from '../app/design/Label'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { ActionButton } from '../common/ListHeader'
import { ShareLinkModal } from '../common/modals/ShareLinkModal'
import { MoreMenuButton } from '../common/MoreMenuButton'
import { EmptyDashboardLayout } from '../dashboards/EmptyDashboardLayout'
import { SettingsLayout } from './SettingsLayout'

type Props = {
    type: ItemSubType
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    switch (base.props.type) {
        default:
            return {
                itemList: await RecordFactory.getInstance().loadItems(
                    base.owner.id!,
                    base.props.type
                ),
            }
    }
}

function ItemMoreMenu({ item, baseUrl }: { item: Item; baseUrl: string }) {
    const user = PSApplication.me()
    const navigate = useNavigate()

    const linkPrefix = item.type === 'product' ? 'shop' : 'book'

    const [showShareLinkModal, setShowShareLinkModal] = useState(false)

    const options = (() => {
        const a = [
            {
                id: 'edit',
                title: `Edit ${item.type ?? ''}`,
                icon: 'pencil' as IconName,
            },

            {
                id: 'copy',
                title: `Copy ${item.type ?? ''}`,
                icon: 'copy' as IconName,
            },
            {
                id: 'share',
                title: 'Share booking link',
                icon: 'share' as IconName,
            },
        ]

        if (item.type === 'class') {
            a.push({
                id: 'lessons',
                title: 'View & edit schedule',
                icon: 'calendar' as IconName,
            })
        }

        return a
    })()

    const formattedItemNameForSharing = (() => {
        return (item.name ?? '')
            .replaceAll('-', '')
            .replaceAll(' ', '-')
            .replaceAll('&', 'and')
            .toLowerCase()
    })()

    return (
        <>
            <div className="flex justify-end">
                <MoreMenuButton
                    options={options}
                    onSelectOption={(option) => {
                        switch (option.id) {
                            case 'edit':
                                navigate(`${baseUrl}/${item.id}/edit`)
                                break
                            case 'share':
                                setShowShareLinkModal(true)
                                break
                            case 'copy':
                                navigate(`${baseUrl}/${item.id}/copy`)
                                break
                            case 'lessons':
                                navigate(`${baseUrl}/${item.id}/lessons`)
                                break
                        }
                    }}
                />
            </div>
            <ShareLinkModal
                uri={`/${linkPrefix}/${user.username || user.key || ''}/item/${formattedItemNameForSharing}`}
                isOpen={showShareLinkModal}
                onClose={() => setShowShareLinkModal(false)}
            />
        </>
    )
}

function ItemIcon({ item }: { item: Item }) {
    if (item.image?.url) {
        return <img src={item.image.url} alt={item.name} className="size-9 rounded-full" />
    }
    return (
        <Icon
            icon={`${getIconNameFromType(item.type ?? '')}.small`}
            shouldInheritColor
            background="round"
            className="text-primary-700 size-9"
        />
    )
}

const render = ({
    data,
    base,
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    const baseUrl = `/settings/${plural(base.props.type)}`

    return (
        <SettingsLayout
            type={base.props.type}
            actionButton={{
                buttonLabel: `Add a ${base.props.type === 'gift' ? 'gift certificate' : base.props.type}`,
                actions: [
                    {
                        id: 'new',
                        label: `Create a new ${base.props.type === 'gift' ? 'gift certificate' : base.props.type}`,
                        href: `${baseUrl}/new`,
                        icon: getIconNameFromType(base.props.type),
                    },
                    {
                        id: 'defaults',
                        label: `Add a ${base.props.type === 'gift' ? 'gift certificate' : base.props.type} from defaults`,
                        href: `${baseUrl}/defaults`,
                        icon: 'documentPen',
                    },
                ],
                icon: 'plus.small',
                intent: 'primary',
            }}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="font-normal">Name</TableHead>
                        <TableHead className="text-right font-normal">Price</TableHead>
                        <TableHead className="font-normal" />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data.itemList.map((item) => (
                        <ClickableTableRow key={item.id} href={`${baseUrl}/${item.id}/edit`}>
                            <TableCell>
                                <div className="flex items-center gap-x-4">
                                    <ItemIcon item={item} />
                                    <Label size="sm">{item.name}</Label>
                                </div>
                            </TableCell>
                            <TableCell className="text-right">
                                {currencyFormat(item.rate, true)}
                            </TableCell>
                            <TableCell>
                                <ItemMoreMenu item={item} baseUrl={baseUrl} />
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {data.itemList.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout
                                dashboard={
                                    new Dashboard({
                                        title: `No ${plural(base.props.type)}`,
                                    })
                                }
                                searchtext={''}
                                actionButton={
                                    <ActionButton
                                        actionButton={{
                                            buttonLabel: `Add a ${base.props.type}`,
                                            icon: 'plus.small',
                                            actions: [
                                                {
                                                    id: 'new',
                                                    label: `Create a new ${base.props.type}`,
                                                    href: `${baseUrl}/new`,
                                                    icon: 'pen',
                                                },
                                                {
                                                    id: 'defaults',
                                                    label: `Add a ${base.props.type} from defaults`,
                                                    href: `${baseUrl}/defaults`,
                                                    icon: getIconNameFromType(base.props.type),
                                                },
                                            ],
                                            intent: 'secondary',
                                        }}
                                    />
                                }
                            />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </SettingsLayout>
    )
}

export const SetupItemsPage = usePSComponent(render, viewWillAppear)
